import {
  getFromLocalStorage,
  setToLocalStorage,
  LOCAL_STORAGE_ITEM,
} from '@utils/localStorageHelper';

// Cross-tab meeting presence is tracked as a heartbeat map in localStorage:
// { [tabId]: lastHeartbeatTimestampMs }
// Any non-stale entry means "a meeting is active in at least one tab".
const TAB_ID_SESSION_KEY = 'videoChatV2MeetingPresenceTabId';
const HEARTBEAT_INTERVAL_MS = 10000;
const HEARTBEAT_TTL_MS = 30000;

let heartbeatInterval = null;
let onPresenceInactive = null;
let storageListenerAttached = false;

const isBrowser = () => typeof window !== 'undefined' && typeof localStorage !== 'undefined';

// Reads the shared heartbeat map; always return a plain object shape.
const readPresenceMap = () => {
  const raw = getFromLocalStorage(LOCAL_STORAGE_ITEM.VIDEO_CHAT_V2_MEETING_PRESENCE);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return raw;
};

const writePresenceMap = (map) => {
  setToLocalStorage(LOCAL_STORAGE_ITEM.VIDEO_CHAT_V2_MEETING_PRESENCE, map);
};

const readPendingAuthActions = () => {
  const raw = getFromLocalStorage(LOCAL_STORAGE_ITEM.VIDEO_CHAT_V2_PENDING_AUTH_ACTIONS);
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw;
};

const writePendingAuthActions = (items) => {
  setToLocalStorage(
    LOCAL_STORAGE_ITEM.VIDEO_CHAT_V2_PENDING_AUTH_ACTIONS,
    Array.isArray(items) ? items : [],
  );
};

// Returns a stable tab-scoped id (sessionStorage is isolated per tab).
const getTabId = () => {
  if (!isBrowser()) {
    return 'server';
  }

  try {
    const existing = window.sessionStorage.getItem(TAB_ID_SESSION_KEY);
    if (existing) {
      return existing;
    }

    const generated = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(TAB_ID_SESSION_KEY, generated);
    return generated;
  } catch (_error) {
    return `tab-${Date.now()}`;
  }
};

// Removes dead/stale tab heartbeats so crashed tabs do not block logout forever.
const pruneStaleEntries = (map, now = Date.now()) => {
  const next = { ...map };
  Object.keys(next).forEach((tabId) => {
    const ts = Number(next[tabId] || 0);
    if (!ts || now - ts > HEARTBEAT_TTL_MS) {
      delete next[tabId];
    }
  });
  return next;
};

// Writes/refreshes this tab's heartbeat while meeting is active.
const upsertOwnHeartbeat = () => {
  if (!isBrowser()) {
    return;
  }

  const tabId = getTabId();
  const now = Date.now();
  const next = pruneStaleEntries(readPresenceMap(), now);
  next[tabId] = now;
  writePresenceMap(next);
};

// Removes this tab from the heartbeat map when meeting ends in this tab.
const removeOwnHeartbeat = () => {
  if (!isBrowser()) {
    return;
  }

  const tabId = getTabId();
  const next = pruneStaleEntries(readPresenceMap(), Date.now());
  if (Object.prototype.hasOwnProperty.call(next, tabId)) {
    delete next[tabId];
    writePresenceMap(next);
  }
};

// Starts periodic heartbeat publishing for the current tab.
export const startVideoChatV2MeetingPresence = () => {
  if (!isBrowser()) {
    return;
  }

  upsertOwnHeartbeat();

  if (heartbeatInterval) {
    return;
  }

  heartbeatInterval = window.setInterval(() => {
    upsertOwnHeartbeat();
  }, HEARTBEAT_INTERVAL_MS);
};

// Stops periodic heartbeat and clears this tab presence entry.
export const stopVideoChatV2MeetingPresence = () => {
  if (!isBrowser()) {
    return;
  }

  if (heartbeatInterval) {
    window.clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }

  removeOwnHeartbeat();
  flushPendingAuthActionsIfMeetingInactive();
};

// True when at least one non-stale tab heartbeat exists.
export const isSharedVideoChatV2MeetingActive = () => {
  if (!isBrowser()) {
    return false;
  }

  const current = readPresenceMap();
  const pruned = pruneStaleEntries(current, Date.now());
  const currentKeys = Object.keys(current);
  const prunedKeys = Object.keys(pruned);

  if (currentKeys.length !== prunedKeys.length) {
    writePresenceMap(pruned);
  }

  return prunedKeys.length > 0;
};

export const setMeetingPresenceInactiveListener = (listener) => {
  onPresenceInactive = typeof listener === 'function' ? listener : null;

  if (!isBrowser() || storageListenerAttached) {
    return;
  }

  window.addEventListener('storage', (event) => {
    if (event?.key !== LOCAL_STORAGE_ITEM.VIDEO_CHAT_V2_MEETING_PRESENCE) {
      return;
    }

    // Another tab may have ended the meeting; replay deferred auth actions.
    flushPendingAuthActionsIfMeetingInactive();
  });

  storageListenerAttached = true;

  // If a pending auth action already exists and no meeting is active, replay now.
  flushPendingAuthActionsIfMeetingInactive();
};

export const queuePendingAuthAction = (type) => {
  if (!isBrowser()) {
    return;
  }

  const normalizedType = String(type || '').trim();
  if (!normalizedType) {
    return;
  }

  const current = readPendingAuthActions();
  if (current.includes(normalizedType)) {
    return;
  }

  current.push(normalizedType);
  writePendingAuthActions(current);
};

export const consumePendingAuthActions = () => {
  if (!isBrowser()) {
    return [];
  }

  const current = readPendingAuthActions();
  writePendingAuthActions([]);
  return current;
};

export const flushPendingAuthActionsIfMeetingInactive = () => {
  if (!isBrowser()) {
    return;
  }

  if (isSharedVideoChatV2MeetingActive()) {
    return;
  }

  if (onPresenceInactive) {
    onPresenceInactive(consumePendingAuthActions());
  }
};
